import React, { memo, useCallback, useState } from 'react'
import CloseIcon from 'transfix-icons/build/CloseIcon'
import EditIcon from 'transfix-icons/build/EditIcon'
import ClipboardCopy from 'transfix-ui/components/Copy'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipmentStopTypes } from 'types/graphqlTypes'
import { IAwardedLaneFragment } from '../../../graphql/AwardedLaneFragment'
import { LaneDetails } from './LaneDetails/LaneDetails'
import { LaneDetailsForm, ILaneDetailsFormProps } from './LaneDetailsForm'
import { IUseLaneDetailsFormProps } from './LaneDetailsForm/hooks/useLaneDetailsForm'
import { WeightType } from './LaneDetailsForm/schema/LaneDetailsSchema'
import { getLaneDetailsClipboardContent } from './helpers'

interface ILaneDetailsTabContentStylesProps {
  isEditing: boolean
}

const useLaneDetailsTabContentStyles = makeStyles<unknown, ILaneDetailsTabContentStylesProps>({
  container: ({ isEditing }) => ({
    border: `1px solid ${brandColors.coolGray3}`,
    padding: 24,
    backgroundColor: isEditing ? brandColors.coolGray2 : undefined,
  }),
  toggleEditBtnContainer: {
    marginLeft: 'auto',
  },
  toggleEditBtn: {
    '& > svg': {
      color: brandColors.skyBlue6,
    },
  },
})

interface ILaneDetailsTabContent {
  awardedLane: IAwardedLaneFragment
}

export const LaneDetailsTabContent = memo<ILaneDetailsTabContent>(({ awardedLane }) => {
  const { listingDetails, shipperRateShipperFacilities } = awardedLane
  const { commodity, maxWeight, minWeight, note, offerLeadTime } = listingDetails ?? {}
  const pickupSrsf = shipperRateShipperFacilities.find(
    srsf => srsf.stopType === IShipmentStopTypes.pickup
  )
  const deliverySrsf = shipperRateShipperFacilities.find(
    srsf => srsf.stopType === IShipmentStopTypes.delivery
  )
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const classes = useLaneDetailsTabContentStyles({ isEditing })
  const { openToast } = useGlobalToast()

  const handleCancelEditingClick = useCallback(() => setIsEditing(false), [])
  const handleStartEditingClick = useCallback(() => setIsEditing(true), [])
  const handleSubmitError: ILaneDetailsFormProps['onSubmitError'] = useCallback(
    errors => {
      const combinedErrorMessages = errors.join('\n')

      openToast({
        toastMessage: `Failed to update lane details: ${combinedErrorMessages}`,
        toastType: 'alert',
      })
    },
    [openToast]
  )
  const handleSubmitSuccess: ILaneDetailsFormProps['onSubmitSuccess'] = useCallback(() => {
    setIsEditing(false)
    openToast({
      toastMessage: 'Successfully updated lane details.',
      toastType: 'default',
    })
  }, [openToast])

  const defaultPickupFacility = pickupSrsf
    ? {
        id: pickupSrsf.shipperFacility.uuid,
        text: pickupSrsf.shipperFacility.facility.name,
      }
    : null
  const defaultDeliveryFacility = deliverySrsf
    ? {
        id: deliverySrsf.shipperFacility.uuid,
        text: deliverySrsf.shipperFacility.facility.name,
      }
    : null

  const defaultFormValues: IUseLaneDetailsFormProps['defaultValues'] = {
    commodity,
    weightType: minWeight && maxWeight ? WeightType.range : WeightType.single,
    minWeight,
    maxWeight,
    offerLeadTime,
    pickupFacility: defaultPickupFacility,
    deliveryFacility: defaultDeliveryFacility,
    note,
  }

  const clipboardContent = getLaneDetailsClipboardContent(awardedLane)

  return (
    <Box mt={3}>
      <Grid className={classes.container} container spacing={2}>
        <Grid container item xs={12}>
          <Grid item>
            <Typography variant='h3'>Lane Details</Typography>
          </Grid>

          <Grid item>
            <Box mt='7px'>
              <ClipboardCopy
                alignClass='right'
                useIcon
                large
                copyText={clipboardContent}
                displayText='Lane Details'
              />
            </Box>
          </Grid>

          <Grid item className={classes.toggleEditBtnContainer}>
            {isEditing ? (
              <IconButton
                Icon={CloseIcon}
                className={classes.toggleEditingButton}
                color='tertiary'
                size='small'
                onClick={handleCancelEditingClick}
                data-test='edit-carrier-button'
              />
            ) : (
              <IconButton
                Icon={EditIcon}
                className={classes.toggleEditingButton}
                color='tertiary'
                size='small'
                onClick={handleStartEditingClick}
                data-test='edit-carrier-button'
              />
            )}
          </Grid>
        </Grid>

        <Grid container item xs={12}>
          <InlineMessage message='These lane details will be shown directly to carrier users in the carrier portal.' />
        </Grid>

        <Grid container item>
          {isEditing ? (
            <LaneDetailsForm
              awardedLane={awardedLane}
              defaultValues={defaultFormValues}
              onCancelClick={handleCancelEditingClick}
              onSubmitError={handleSubmitError}
              onSubmitSuccess={handleSubmitSuccess}
            />
          ) : (
            <LaneDetails awardedLane={awardedLane} />
          )}
        </Grid>
      </Grid>
    </Box>
  )
})

export default LaneDetailsTabContent
